import React from "react";
import { motion } from "framer-motion";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../../../misc/Accordion";
import { getPlatformIcon } from "../../../../../utils/logo-getter";
import { useIsMobile } from "../../../../../hooks/useIsMobile";

// Define a TypeScript interface for help topics
export interface HelpItem {
    title: string;
    content: JSX.Element;
    icon: JSX.Element | null;
}

// Social media help topics data
const socialMediaHelpData: HelpItem[] = [
    {
        title: "Facebook",
        content: (
            <div className="space-y-4">
                <p>When connecting Facebook:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>Open a new tab and log in to your Facebook account.</li>
                    <li>
                        Make sure that you have a Facebook Page or create one.
                    </li>
                    <li>
                        Ensure you have manager access to the Facebook Page.
                    </li>
                    <li>
                        Click on Facebook on Ayrshare, where you will be
                        prompted to authorize a connection to your Facebook
                        account.
                    </li>
                    <li>
                        Once authorizing the connection through the Facebook
                        window, you will be prompted to choose a page from the
                        list. Choose the page for your posts.
                    </li>
                </ul>
                <p className="text-sm text-red-500">
                    <strong>Important: </strong>You must be using your Facebook
                    personal profile during the connection process. Do not
                    switch to or act as a Page while authorizing — this will
                    cause errors or failed connections.
                </p>
            </div>
        ),
        icon: (
            <div className="flex space-x-2">{getPlatformIcon("Facebook")}</div>
        ),
    },
    {
        title: "Instagram",
        content: (
            <div className="space-y-4">
                <p>When connecting Instagram:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        Go to{" "}
                        <a
                            className="text-blue-500"
                            href="https://business.facebook.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Facebook Business Manager
                        </a>{" "}
                        and connect your Instagram account to your Facebook
                        page.
                    </li>
                    <li>
                        Once connected, go to Ayrshare page and click on
                        Instagram to authorize the connection.
                    </li>
                    <li>
                        You will go through the same process as Facebook to
                        authorize the connection. When prompted to choose the
                        page, select the one your Instagram account is connected
                        to.
                    </li>
                </ul>
            </div>
        ),
        icon: (
            <div className="flex space-x-2">{getPlatformIcon("Instagram")}</div>
        ),
    },
    {
        title: "X (formerly Twitter)",
        content: (
            <div className="space-y-4">
                <p>
                    To successfully connect your X (formerly Twitter) account
                    through Ayrshare, please follow these steps:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        Ensure you are logged into the *exact* X account you
                        want to post from — in a separate browser tab before
                        continuing.
                    </li>
                    <li>
                        Do <strong>not</strong> use an account that has
                        permission to act on behalf of another — you must be
                        logged in directly to the posting account.
                    </li>
                    <li>
                        On the Ayrshare dashboard, click the X icon to begin the
                        connection process.
                    </li>
                    <li>
                        You’ll be redirected to X to authorize access — confirm
                        the connection for the logged-in account.
                    </li>
                    <li>
                        Once authorized, you'll return to Ayrshare
                        automatically.
                    </li>
                </ul>
                <p className="text-sm text-red-500">
                    <strong>Important: </strong>If you’re logged into one X
                    account but try to act on behalf of another, the connection
                    will fail. X does not allow delegated posting in this flow.
                    You may see an error and get stuck — in that case, click
                    “Back” and retry the connection using the correct X account.
                </p>
            </div>
        ),
        icon: (
            <div className="flex space-x-2">{getPlatformIcon("Twitter")}</div>
        ),
    },

    {
        title: "LinkedIn",
        content: (
            <div className="space-y-4">
                <p>For LinkedIn connections:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        Make sure you are logged into your LinkedIn account in a
                        new tab.
                    </li>
                    <li>Click on LinkedIn on the Ayrshare page.</li>
                    <li>
                        You will be prompted to choose between a Company
                        LinkedIn Page or a Personal LinkedIn page.
                    </li>
                    <li>
                        If you choose Company Page, you will be asked to choose
                        a company page. Once chosen, the prompt will close and
                        the Company Page is connected to Marabot.
                    </li>
                    <li>
                        Ensure you have manager access to the LinkedIn Page.
                    </li>
                    <li>
                        If you chose Personal Page, your personal account will
                        be connected to Marabot.
                    </li>
                </ul>
            </div>
        ),
        icon: (
            <div className="flex space-x-2">{getPlatformIcon("LinkedIn")}</div>
        ),
    },
    {
        title: "Pinterest",
        content: (
            <div className="space-y-4">
                <p>For Pinterest:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        Make sure you are logged into your Pinterest account in
                        a new tab.
                    </li>
                    <li>Make sure you created a board in Pinterest.</li>
                    <li>Click on Pinterest on the Ayrshare page.</li>
                    <li>
                        You will be redirected to Pinterest to authorize the
                        connection. Once authorized, you will be redirected back
                        to the Ayrshare page.
                    </li>
                    <li>
                        You will be prompted to choose a board from your list of
                        boards.
                    </li>
                    <li>
                        Choose a board and click submit to connect Pinterest to
                        Marabot.
                    </li>
                </ul>
            </div>
        ),
        icon: (
            <div className="flex space-x-2">{getPlatformIcon("Pinterest")}</div>
        ),
    },
    {
        title: "Google My Business",
        content: (
            <div className="space-y-4">
                <p>For Google My Business:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        Make sure you are logged into your Google account in a
                        new tab.
                    </li>
                    <li>Click on Google My Business on the Ayrshare page.</li>
                    <li>
                        You will be redirected to Google to authorize the
                        connection.
                    </li>
                    <li>
                        Choose the account that is connected to your business
                        profile and authorize the connection.
                    </li>
                    <li>
                        You will be redirected back to the Ayrshare page where
                        you’ll be prompted to choose a business profile.
                    </li>
                    <li>
                        Choose a business profile and click submit to connect
                        Google My Business to Marabot.
                    </li>
                    <li>
                        Make sure that the connected account has owner or
                        manager permissions for the business profiles.
                    </li>
                </ul>
            </div>
        ),
        icon: <div className="flex space-x-2">{getPlatformIcon("Google")}</div>,
    },
    {
    title: "YouTube",
    content: (
        <div className="space-y-4">
            <p>For YouTube connections:</p>
            <ul className="list-disc pl-5 space-y-2">
                <li>
                    Make sure you are logged into the Google account that
                    manages your YouTube channel in a new browser tab.
                </li>
                <li>
                    Ensure that the Google account has access to the YouTube
                    channel you want to post to.
                </li>
                <li>
                    On the Ayrshare dashboard, click on YouTube to begin the
                    connection process.
                </li>
                <li>
                    You will be redirected to Google to authorize access.
                </li>
                <li>
                    Select the correct Google account and approve the
                    permissions requested.
                </li>
                <li>
                    After authorization, you will be redirected back to the
                    Ayrshare page and the YouTube channel will be connected to
                    Marabot.
                </li>
                <li>
                    To enable custom thumbnails, verify
                    your phone number in YouTube Studio → Settings → Channel →
                    Feature Eligibility.
                </li>
                <li>
                    To enable clickable links in video descriptions, activate
                    Advanced Features in YouTube Studio → Settings → Channel →
                    Feature Eligibility.
                </li>
            </ul>
            <p className="text-sm text-red-500">
                <strong>Important: </strong>The connected Google account must
                have owner or manager access to the YouTube channel. If you
                authorize with a different Google account, the connection may
                fail or the channel may not appear.
            </p>
        </div>
    ),
    icon: (
        <div className="flex space-x-2">{getPlatformIcon("Youtube")}</div>
    ),
},
    {
        title: "TikTok",
        content: (
            <div className="space-y-4">
                <p>
                    Marabot does not post to TikTok yet. Connecting TikTok does
                    not add it to Add Post, campaigns or Auto Pilot.
                </p>
                <p>For TikTok:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>
                        You can connect either a personal or a business TikTok
                        account.
                    </li>
                    <li>
                        Make sure you are logged into your TikTok account in a
                        new tab.
                    </li>
                    <li>Click on TikTok on the Ayrshare page.</li>
                    <li>
                        You will be redirected to TikTok to log in and
                        authorize the connection.
                    </li>
                    <li>
                        After authorization, you will be redirected back to the
                        Ayrshare page and your TikTok account will be connected
                        to Marabot.
                    </li>
                </ul>
                <p className="text-sm text-red-500">
                    <strong>Important: </strong>TikTok requires the connection
                    to be renewed every 365 days. If your TikTok account shows
                    as not connected, connect it again.
                </p>
            </div>
        ),
        icon: <div className="flex space-x-2">{getPlatformIcon("TikTok")}</div>,
    },
];

// Social Media Help Component
const SocialMediaHelp: React.FC = () => {
    const isMobile = useIsMobile(900);

    return (
        <div
            className={`mt-2 bg-white ${
                isMobile
                    ? "w-full"
                    : "max-w-4xl flex justify-center items-center"
            } mx-auto rounded-lg shadow-lg`}
        >
            <div className={`w-full ${isMobile ? "py-4" : "py-8"}`}>
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-3xl mx-auto"
                >
                    <Accordion type="single" collapsible className="space-y-4">
                        {socialMediaHelpData.map((help, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.3,
                                    delay: index * 0.1,
                                }}
                            >
                                <AccordionItem
                                    value={`item-${index}`}
                                    className="bg-white rounded-lg shadow-md"
                                >
                                    <AccordionTrigger className="px-4 py-3 text-left text-gray-900 hover:text-gray-600 transition-colors duration-200">
                                        {help.icon && (
                                            <span className="mr-2">
                                                {help.icon}
                                            </span>
                                        )}
                                        <span className="font-semibold text-primary">
                                            {help.title}
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 py-3 text-gray-700">
                                        {help.content}
                                    </AccordionContent>
                                </AccordionItem>
                            </motion.div>
                        ))}
                    </Accordion>
                </motion.div>
            </div>
        </div>
    );
};

export default SocialMediaHelp;
